import { create } from "zustand";
import type { ComposerAttachment } from "./attachments";

/**
 * The in-builder AI surfaces. The first three are Craft.js block canvases; the
 * last three are flat-JSON config builders (calendar/wallet/portal) where the
 * AI edits config fields via a read/apply-patch tool pair instead of blocks.
 */
export type BuilderSurface =
  | "email"
  | "document"
  | "website"
  | "calendar"
  | "wallet"
  | "portal";

/**
 * The creation-wizard AI surfaces (full-screen create flows with an embedded
 * Velo copilot). Sent as `wizard:<kind>` so the backend exposes the
 * surface-gated read_/apply_*_wizard_state tools + the interview hint, and
 * suppresses the immediate-create server tools while the wizard stages a draft.
 */
export type WizardSurface =
  | "email-template"
  | "document"
  | "slotting-offering"
  | "workflow"
  // Create-event wizard — the only COMPANY-scoped wizard surface (no event
  // exists yet); served by the company chat lane, not the event one.
  | "event-create"
  // Website create wizard (WebsiteCreateWizard, event variant) — C-4.
  | "website-create"
  // Attendee-app create wizard (AppCreateWizard, first-run /app-builder setup).
  | "app-create"
  // First-run Goals & KPIs setup wizard (GoalsSetupWizard).
  | "goals-setup"
  // Setup Hub "Adjust with Velo" — one section-scoped proposal editor per
  // buildable section card (backend hint keyed on `wizard:setup-<section>`).
  | "setup-emails"
  | "setup-workflows"
  | "setup-offering"
  | "setup-website";

/**
 * Tiny cross-tree store for AI-assistant availability + the drawer/composer
 * state that must outlive the provider's unmount/remount on a shell swap. The
 * AppBar's AI button lives OUTSIDE the assistant provider on event pages
 * (EventLayoutShell renders SAppBar above EventScopedAiAssistant), so it can't
 * read the context — the provider publishes "can the drawer open" here instead.
 * Mirrors src/lib/onboarding/coach-store.ts.
 */
type AiAvailabilityState = {
  /** true while a mounted provider has scope resolved + capability/role. */
  available: boolean;
  /**
   * Drawer open flag. Lives here (not in the provider's reducer) so it
   * survives the dashboard↔event shell swap: the provider unmounts with its
   * shell and a reducer-held flag would reset, closing the drawer on every
   * navigation between the company dashboard and an event. Module-level
   * state also bridges the gap where NO provider is mounted mid-swap.
   * Transcript state stays per-provider on purpose (resets per scope).
   */
  drawerOpen: boolean;
  /**
   * Which in-builder AI surface is live, or null = not in a builder. Set by
   * BuilderScopedAiAssistant while a builder is mounted; read by the provider's
   * currentSurface() to send `builder:<kind>` so the backend exposes the
   * surface-gated read/apply tools and the system-prompt hint. The builder
   * routes live OUTSIDE EventLayoutShell. The block-canvas builders (email/
   * document/website) expose block read/apply tools; the config builders
   * (calendar/wallet/portal) expose read/apply config-patch tools instead.
   */
  builderSurface: BuilderSurface | null;
  /**
   * Which creation-wizard AI surface is live, or null = no wizard open. Set by
   * WizardScopedAiAssistant while a wizard is mounted; wins over builderSurface
   * in the provider's currentSurface() (a wizard is a full-screen overlay, so
   * it is always the user's actual context while mounted).
   */
  wizardSurface: WizardSurface | null;
  /**
   * One-shot flag set by the `open_template_in_builder` AI tool right before it
   * deep-links the browser into a builder route: it tells the builder body to
   * auto-open its AI panel on mount (so the user lands with the in-builder
   * assistant ready, not a shut panel). Consumed-and-cleared once on read via
   * consumePendingBuilderAiOpen() so a normal builder open stays closed.
   */
  pendingBuilderAiOpen: boolean;
  /**
   * Half-typed composer text. The drawer's AiChatPanel unmounts with its
   * shell, so the draft lives here instead (cleared by the provider together
   * with the transcript on scope change).
   */
  composerDraft: string;
  /**
   * Pending image attachments for the composer. Same lifecycle as the draft —
   * they belong to the transcript, so the provider clears them on a scope
   * change / unmount and the composer clears them on an accepted send. Each
   * holds an object URL minted on add and revoked on remove/clear (no leaks);
   * lives here (not local to the bar) so it survives the shell swap.
   */
  composerAttachments: ComposerAttachment[];
  /**
   * The raw image Files the user attached to the LAST sent turn. The composer
   * clears its own attachments on an accepted send (revoking their object URLs),
   * but the in-builder `upload_attached_image_to_*` client tool runs LATER in
   * that same turn and needs the bytes to upload to R2 — so the Composer stamps
   * the sent Files here at send time. Overwritten by every send (a text-only
   * send sets it to []), and cleared with the transcript on a scope change /
   * unmount. No object URLs here — these are upload sources, not previews.
   */
  turnAttachments: File[];
  /**
   * Bumped whenever the chat is reset to a fresh conversation (the provider's
   * `newConversation`). Cross-tree, turn-independent client tools that stash
   * state ACROSS turns within one conversation (e.g. AiImportTools' staged
   * spreadsheet) watch this to drop that state on a reset — otherwise a file
   * staged in conversation A could be imported in conversation B (same event,
   * so no eventId-change remount fires).
   */
  chatResetNonce: number;
  /**
   * Event id whose wizard→event Velo handoff is still UNSEEN — drives the
   * badge dot on the AppBar AI button (which lives OUTSIDE the provider on
   * event pages, so it can't read context). Set by the provider when it
   * mounts an event scope with a pending handoff (see velo-handoff.ts);
   * cleared when the drawer is opened on that event (seen) or the handoff
   * is dismissed/consumed.
   */
  handoffBadgeEventId: string | null;
  /**
   * Ref-count of mounted full-screen immersive surfaces (WizardShell). While
   * > 0, `setDrawerOpen(true)` is a NO-OP: the drawer's 340px rail (z-10000)
   * would cover the wizard's right column incl. its footer nav, and the wizard
   * is aria-modal + focus-trapped, so a rail beside it is unreachable anyway.
   * Gating at the store is the single choke point — it catches EVERY open path
   * (⌘J toggle, the `openAiAssistant` window event, openDrawer() callers)
   * without each having to know about wizards. Closing is never gated.
   * NOT the same as coach-store.overlayCount: that hides bottom-right floaters
   * for any modal; this only blocks the drawer, only for fullscreen surfaces.
   */
  immersiveOverlayCount: number;
  setAvailable: (available: boolean) => void;
  setDrawerOpen: (drawerOpen: boolean) => void;
  pushImmersiveOverlay: () => void;
  popImmersiveOverlay: () => void;
  setBuilderSurface: (builderSurface: BuilderSurface | null) => void;
  setWizardSurface: (wizardSurface: WizardSurface | null) => void;
  setPendingBuilderAiOpen: (pendingBuilderAiOpen: boolean) => void;
  setComposerDraft: (composerDraft: string) => void;
  /** Mints an object URL per file and appends (caller pre-validates type/size/count). */
  addComposerAttachments: (files: File[]) => void;
  /** Removes one attachment and revokes its object URL. */
  removeComposerAttachment: (id: string) => void;
  /** Drops all attachments, revoking every object URL. */
  clearComposerAttachments: () => void;
  /** Records the Files sent with the current turn (for the in-builder upload tool). */
  setTurnAttachments: (files: File[]) => void;
  setHandoffBadgeEventId: (handoffBadgeEventId: string | null) => void;
  /** Marks a fresh-conversation reset: bumps chatResetNonce and drops the last
   *  turn's upload sources. Cross-turn client-tool state keys off the nonce. */
  bumpChatReset: () => void;
};

// The drawer-open flag is mirrored to sessionStorage so a full page reload
// reopens the assistant if it was open (it's the only persisted bit — the
// transcript is not). sessionStorage (not localStorage) scopes it to the tab
// session, so closing the tab starts fresh. Best-effort: blocked/absent storage
// just means no reopen, never a throw.
const DRAWER_OPEN_KEY = "veltric:ai-drawer-open";

function safeSessionStorage(): Storage | null {
  try {
    const ss = typeof window !== "undefined" ? window.sessionStorage : null;
    return ss && typeof ss.getItem === "function" && typeof ss.setItem === "function"
      ? ss
      : null;
  } catch {
    return null;
  }
}

function readDrawerOpen(): boolean {
  return safeSessionStorage()?.getItem(DRAWER_OPEN_KEY) === "1";
}

function writeDrawerOpen(open: boolean): void {
  const ss = safeSessionStorage();
  if (!ss) return;
  try {
    if (open) ss.setItem(DRAWER_OPEN_KEY, "1");
    else ss.removeItem(DRAWER_OPEN_KEY);
  } catch {
    /* best-effort */
  }
}

function newId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `att-${Math.random().toString(36).slice(2)}`;
}

function makeObjectUrl(file: File): string {
  return typeof URL !== "undefined" && URL.createObjectURL
    ? URL.createObjectURL(file)
    : "";
}

function revokeObjectUrl(url: string): void {
  if (url && typeof URL !== "undefined" && URL.revokeObjectURL) URL.revokeObjectURL(url);
}

export const useAiAvailabilityStore = create<AiAvailabilityState>((set) => ({
  available: false,
  // Seeded from sessionStorage so a reload reopens the drawer if it was open.
  // The provider's `available && scope` gate still decides if it actually
  // mounts, so a persisted-open flag never shows the drawer on a page that
  // shouldn't have it — it just reappears once availability resolves.
  drawerOpen: readDrawerOpen(),
  builderSurface: null,
  wizardSurface: null,
  pendingBuilderAiOpen: false,
  composerDraft: "",
  composerAttachments: [],
  turnAttachments: [],
  chatResetNonce: 0,
  handoffBadgeEventId: null,
  immersiveOverlayCount: 0,
  setAvailable: (available) =>
    set((s) => (s.available === available ? s : { available })),
  setDrawerOpen: (drawerOpen) =>
    set((s) => {
      // Opens are swallowed while an immersive surface owns the screen (see
      // immersiveOverlayCount doc). Also skips the sessionStorage write, so a
      // blocked open can't resurrect the drawer on the next reload.
      if (drawerOpen && s.immersiveOverlayCount > 0) return s;
      if (s.drawerOpen === drawerOpen) return s;
      writeDrawerOpen(drawerOpen);
      return { drawerOpen };
    }),
  pushImmersiveOverlay: () =>
    set((s) => ({ immersiveOverlayCount: s.immersiveOverlayCount + 1 })),
  popImmersiveOverlay: () =>
    set((s) => ({ immersiveOverlayCount: Math.max(0, s.immersiveOverlayCount - 1) })),
  setBuilderSurface: (builderSurface) =>
    set((s) => (s.builderSurface === builderSurface ? s : { builderSurface })),
  setWizardSurface: (wizardSurface) =>
    set((s) => (s.wizardSurface === wizardSurface ? s : { wizardSurface })),
  setPendingBuilderAiOpen: (pendingBuilderAiOpen) =>
    set((s) => (s.pendingBuilderAiOpen === pendingBuilderAiOpen ? s : { pendingBuilderAiOpen })),
  setComposerDraft: (composerDraft) =>
    set((s) => (s.composerDraft === composerDraft ? s : { composerDraft })),
  addComposerAttachments: (files) =>
    set((s) => {
      if (files.length === 0) return s;
      const added = files.map((file) => ({ id: newId(), file, url: makeObjectUrl(file) }));
      return { composerAttachments: [...s.composerAttachments, ...added] };
    }),
  removeComposerAttachment: (id) =>
    set((s) => {
      const target = s.composerAttachments.find((a) => a.id === id);
      if (!target) return s;
      revokeObjectUrl(target.url);
      return { composerAttachments: s.composerAttachments.filter((a) => a.id !== id) };
    }),
  clearComposerAttachments: () =>
    set((s) => {
      if (s.composerAttachments.length === 0) return s;
      for (const a of s.composerAttachments) revokeObjectUrl(a.url);
      return { composerAttachments: [] };
    }),
  setTurnAttachments: (files) =>
    set((s) => (s.turnAttachments.length === 0 && files.length === 0 ? s : { turnAttachments: files })),
  setHandoffBadgeEventId: (handoffBadgeEventId) =>
    set((s) =>
      s.handoffBadgeEventId === handoffBadgeEventId ? s : { handoffBadgeEventId },
    ),
  bumpChatReset: () =>
    set((s) => {
      // Staged-but-unsent composer previews must not leak into the next
      // conversation (and their object URLs must be revoked).
      for (const a of s.composerAttachments) revokeObjectUrl(a.url);
      return {
        chatResetNonce: s.chatResetNonce + 1,
        turnAttachments: [],
        composerAttachments: [],
      };
    }),
}));

/**
 * Reads-and-clears the one-shot pendingBuilderAiOpen flag. A builder body calls
 * this in its `aiPanel` useState initializer: returns true (and resets the flag)
 * exactly once after the open_template_in_builder tool deep-linked here, so the
 * AI panel auto-opens on a deliberate deep-link but stays closed on a normal open.
 */
export function consumePendingBuilderAiOpen(): boolean {
  const store = useAiAvailabilityStore.getState();
  if (!store.pendingBuilderAiOpen) return false;
  store.setPendingBuilderAiOpen(false);
  return true;
}
